import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import type { PurchaseRequisition } from '../../types/purchasing';
import { searchPurchaseRequisitions } from '../../utils/purchasingSearch';

interface PurchaseSearchOverlayProps {
  rows: PurchaseRequisition[];
  onClose: () => void;
  onOpen: (id: string) => void;
}

const PurchaseSearchOverlay: React.FC<PurchaseSearchOverlayProps> = ({ rows, onClose, onOpen }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => searchPurchaseRequisitions(rows, query), [rows, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    document.getElementById(`po-search-hit-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, hits.length]);

  const openHit = (id: string) => {
    onOpen(id);
    onClose();
  };

  return (
    <div className="po-search-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="po-search-panel"
        role="dialog"
        aria-label="Search purchase orders"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(Math.max(hits.length - 1, 0), i + 1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            const hit = hits[active];
            if (hit) openHit(hit.id);
          }
        }}
      >
        <div className="po-search-input-row">
          <Search size={16} className="po-search-icon" aria-hidden />
          <input
            ref={inputRef}
            className="po-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search amount, PO, vendor, or line items"
            aria-label="Search purchase orders"
          />
          <kbd className="po-search-kbd">Esc</kbd>
        </div>
        <div className="po-search-hint">
          Matches amount, PR, PO, vendor, job, and each line. Ignores the status filters.
        </div>
        <div className="po-search-results" role="listbox">
          {query.trim() && hits.length === 0 ? (
            <p className="po-search-empty">No matching purchase orders.</p>
          ) : null}
          {!query.trim() ? (
            <p className="po-search-empty">Type an amount, line, vendor, PO number, or job.</p>
          ) : null}
          {hits.map((hit, index) => (
            <button
              key={hit.id}
              id={`po-search-hit-${index}`}
              type="button"
              role="option"
              aria-selected={index === active}
              className={`po-search-hit${index === active ? ' po-search-hit--active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => openHit(hit.id)}
            >
              <span className="po-search-hit-top">
                <span className="po-search-hit-num">{hit.poNumber || hit.prNumber}</span>
                {hit.poNumber ? <span className="po-search-hit-pr">{hit.prNumber}</span> : null}
                <span className="po-search-hit-vendor">{hit.vendorName || 'No vendor'}</span>
                <span className="fin-pill po-search-hit-status">{hit.statusLabel}</span>
              </span>
              <span className="po-search-hit-match">
                <span className="po-search-hit-label">{hit.matchLabel}</span>
                {hit.matchText}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PurchaseSearchOverlay;
